import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios';

const API_URL = import.meta.env.VITE_API_URL || '/api';

export const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
});

// Request interceptor - add JWT token
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('token');
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  },
);

// Response interceptor - handle errors
api.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      // Redirect to login if not already there
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  },
);

export interface ApiError {
  error: string;
  message: string;
  statusCode: number;
  details?: unknown;
}

export function isApiError(error: unknown): error is AxiosError<ApiError> {
  return axios.isAxiosError(error) && error.response?.data?.error !== undefined;
}

// Prefer the backend message over Axios' generic "Request failed with status code X"
export function getApiErrorMessage(error: unknown, fallback: string): string {
  if (isApiError(error) && error.response?.data?.message) {
    return error.response.data.message;
  }
  return fallback;
}

// Helper function for fetch with authentication
export async function fetchWithAuth(url: string, options: RequestInit = {}): Promise<Response> {
  const token = localStorage.getItem('token');
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...((options.headers as Record<string, string>) ?? {}),
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(url, {
    ...options,
    headers,
  });

  if (response.status === 401) {
    localStorage.removeItem('token');
    if (window.location.pathname !== '/login') {
      window.location.href = '/login';
    }
  }

  return response;
}

// Helper function to get user from JWT token
export function getUserFromToken(): { id: string; name: string } | null {
  const token = localStorage.getItem('token');
  if (!token) return null;

  try {
    // JWT token format: header.payload.signature
    const payload = token.split('.')[1];
    if (!payload) return null;

    // Decode base64url to base64
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    // Add padding if needed
    const paddedBase64 = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');

    // Parse JSON
    const decoded = JSON.parse(atob(paddedBase64));

    return {
      id: decoded.sub || decoded.id || decoded.userId || '',
      name: decoded.name || decoded.username || decoded.email || 'Unknown',
    };
  } catch (error) {
    console.error('Failed to decode token:', error);
    return null;
  }
}
